import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { FaTimes } from "react-icons/fa";

import myConfig from "../../configs/config";
import { applyGradualMapZoom } from "../../utils/gradualMapZoom";
import { getMapOptions } from "./rumoPistaUtils";
import { fetchGroupLandPlots } from "./serviceOrderLocationApi";
import "./ServiceOrderLandplotMapModal.css";

function plotRing(plot) {
  if (!plot || !plot.geomap) return [];
  try {
    const geo = typeof plot.geomap === "string" ? JSON.parse(plot.geomap) : plot.geomap;
    const ring = geo && geo.coordinates && geo.coordinates[0];
    if (!Array.isArray(ring)) return [];
    return ring
      .map((pair) => ({
        lat: Number(pair[1]),
        lng: Number(pair[0]),
      }))
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  } catch (error) {
    return [];
  }
}

function plotCenter(plot, ring) {
  if (plot && plot.center && Number.isFinite(Number(plot.center.lat))) {
    return { lat: Number(plot.center.lat), lng: Number(plot.center.lng) };
  }
  if (!ring.length) return { lat: -15.78, lng: -47.93 };
  const lat = ring.reduce((sum, point) => sum + point.lat, 0) / ring.length;
  const lng = ring.reduce((sum, point) => sum + point.lng, 0) / ring.length;
  return { lat, lng };
}

function ServiceOrderLandplotMapModal({
  show,
  farmId,
  farmName,
  selectedNames,
  onHide,
  onPick,
  t,
}) {
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const polygonsRef = useRef(new Map());
  const selectedIdsRef = useRef(selectedIds);
  const seedNamesRef = useRef(selectedNames);
  selectedIdsRef.current = selectedIds;
  if (!show) seedNamesRef.current = selectedNames;

  useEffect(() => {
    if (!show || !farmId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchGroupLandPlots(farmId)
      .then((rows) => {
        if (!cancelled) setPlots(rows || []);
      })
      .catch(() => {
        if (!cancelled) setPlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, farmId]);

  const drawn = useMemo(
    () =>
      (plots || [])
        .map((plot) => ({ plot, ring: plotRing(plot) }))
        .filter((item) => item.ring.length >= 3),
    [plots]
  );

  const visible = farmName
    ? drawn.filter(
        (item) =>
          !item.plot.farm_name ||
          String(item.plot.farm_name).toLowerCase() ===
            String(farmName).toLowerCase()
      )
    : drawn;

  const center = plotCenter(
    visible[0] && visible[0].plot,
    (visible[0] && visible[0].ring) || []
  );

  function paintPolygons() {
    const marked = new Set(selectedIdsRef.current);
    polygonsRef.current.forEach((polygon, id) => {
      const on = marked.has(id);
      polygon.setOptions({
        strokeColor: on ? "#1ABB9C" : "#F4D03F",
        fillColor: on ? "#1ABB9C" : "#F4D03F",
        fillOpacity: on ? 0.55 : 0.28,
      });
    });
  }

  useEffect(() => {
    if (!show) return;
    const names = new Set(seedNamesRef.current || []);
    setSelectedIds(
      plots.filter((plot) => names.has(plot.name)).map((plot) => plot.id)
    );
  }, [plots, show]);

  useEffect(() => {
    paintPolygons();
  }, [selectedIds]);

  function togglePlot(plotId) {
    setSelectedIds((current) =>
      current.includes(plotId)
        ? current.filter((id) => id !== plotId)
        : current.concat(plotId)
    );
  }

  function confirmSelection() {
    const chosen = visible
      .map((item) => item.plot)
      .filter((plot) => selectedIds.includes(plot.id))
      .map((plot) => ({
        id: plot.id,
        name: plot.name,
        farmName: plot.farm_name || farmName || "",
        picture: plot.picture || "",
      }));
    onPick(chosen);
  }

  function handleApiLoaded({ map, maps }) {
    applyGradualMapZoom(map);
    polygonsRef.current = new Map();
    const bounds = new maps.LatLngBounds();
    visible.forEach(({ plot, ring }) => {
      const polygon = new maps.Polygon({
        paths: ring,
        map,
        strokeColor: "#F4D03F",
        strokeWeight: 2,
        fillColor: "#F4D03F",
        fillOpacity: 0.28,
        clickable: true,
      });
      polygonsRef.current.set(plot.id, polygon);
      ring.forEach((point) => bounds.extend(point));
      polygon.addListener("click", () => togglePlot(plot.id));
    });
    if (!bounds.isEmpty()) map.fitBounds(bounds);
    paintPolygons();
  }

  if (!show) return null;

  return (
    <div className="service-order-plot-modal" role="dialog" aria-modal="true">
      <div className="service-order-plot-modal__backdrop" onClick={onHide} />
      <div className="service-order-plot-modal__panel">
        <header className="service-order-plot-modal__header">
          <div>
            <strong>{t("Escolher talhão no mapa")}</strong>
            <p>{t("Marque um ou mais talhões no mapa ou na lista. Depois confirme.")}</p>
          </div>
          <div className="service-order-plot-modal__actions">
            <button type="button" className="btn btn-success" onClick={confirmSelection}>
              {t("Usar talhões marcados")} ({selectedIds.length})
            </button>
            <button type="button" className="btn btn-default" onClick={onHide}>
              <FaTimes style={{ marginRight: 6 }} />
              {t("Fechar")}
            </button>
          </div>
        </header>
        <div className="service-order-plot-modal__body">
          <div className="service-order-plot-modal__map">
            {loading ? (
              <p>{t("Carregando talhões...")}</p>
            ) : (
              <GoogleMapReact
                bootstrapURLKeys={{ key: myConfig.GOOGLE_API_KEY }}
                defaultCenter={center}
                defaultZoom={14}
                yesIWantToUseGoogleMapApiInternals
                options={getMapOptions}
                onGoogleApiLoaded={handleApiLoaded}
              />
            )}
          </div>
          <ul className="service-order-plot-modal__list">
            {visible.map(({ plot }) => (
              <li key={plot.id}>
                <button
                  type="button"
                  className={
                    selectedIds.includes(plot.id)
                      ? "service-order-plot-modal__item service-order-plot-modal__item--active"
                      : "service-order-plot-modal__item"
                  }
                  onClick={() => togglePlot(plot.id)}
                >
                  {plot.picture ? (
                    <img src={plot.picture} alt="" />
                  ) : (
                    <span className="service-order-plot-modal__swatch" />
                  )}
                  <span>
                    <strong>{plot.name}</strong>
                    <small>{plot.farm_name}</small>
                  </span>
                </button>
              </li>
            ))}
            {!loading && visible.length === 0 ? (
              <li>{t("Nenhum talhão desenhado nesta fazenda.")}</li>
            ) : null}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ServiceOrderLandplotMapModal;
